import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSavedSearches, type SearchEntry } from '../redux/features/savedSearchesSlice';
import {
  canonicalSavedSearchPath,
  computeSavedSearchSyncPlan,
} from '../utils/saved-searches/compute-saved-search-sync-plan';
import {
  parseServerSavedSearches,
  parseServerSavedSearchFolders,
  type ServerSavedSearch,
  type ServerSavedSearchFolder,
} from '../utils/saved-searches/server-saved-searches';
import { savedSearchAlertId } from '../utils/saved-searches/saved-search-alert-id';
import { findSavedSearchForUrl } from '../utils/saved-searches/find-saved-search-for-url';
import {
  clearSavedSearchDeleted,
  isRecentlySavedSearchDeleted,
  recordSavedSearchDeleted,
} from '../utils/saved-searches/deleted-search-tombstones';
import type { SavedSearchFolderData } from '../types/email-alerts';
import {
  SAVED_SEARCH_FOLDER_ITEM_KEY_PREFIX,
  SAVED_SEARCH_ITEM_KEY_PREFIX,
} from '../types/email-alerts';
import type { UserStateSavedSearchFolderKey, UserStateSavedSearchKey } from '../types/user-state';
import { useUserState } from '../query/UserProvider';
import { useUpdateUserStateItem } from '../query/user-state/useUpdateUserStateItem';
import { useDeleteUserStateItem } from '../query/user-state/useDeleteUserStateItem';

/**
 * Account persistence for saved searches (spec §8): on load, upload local-only
 * searches to UserState and pull server-only ones (other devices) into redux;
 * afterwards, UI mutations (alert toggle, rename, delete, folder, order) write
 * through to the server. Deletions are direct — the sync pass itself never deletes.
 *
 * TEC-404: migrated to the shared TanStack Query user-state layer
 * (`query/user-state/useUpdateUserStateItem.ts` / `useDeleteUserStateItem.ts` / `useUserState()`)
 * instead of its own bespoke raw-`fetch` + local component state. `items`/`folders` are now pure
 * derivations of `useUserState().data` (the same cache every other migrated hook reads/writes),
 * so a search saved from another tab/hook updates this hook's view for free. Two behavioral notes
 * versus the pre-migration version:
 * - `mutateItem`/`upsertFolder` no longer retry-with-merge on a 409 conflict — they adopt the
 *   server's winning entry (written into the cache by `useUpdateUserStateItem`'s `onError`) and
 *   report failure, same simplification the rest of TEC-404's migrated write hooks made
 *   (`useApplicationKitActions.ts`, `useProfilePreferencesSettingsSync.ts`).
 * - `deleteItem`/`deleteFolder` now roll back to the previous entry in the shared cache if the
 *   DELETE actually fails (`useDeleteUserStateItem`'s `onError`), instead of leaving the item
 *   removed client-side forever until the next sync pass happens to pull it back — a small
 *   correctness improvement that falls out of using the shared mutation lifecycle.
 *
 * The one-shot upload/pull pass no longer does its own `GET /api/user-state` — `useUserState()`
 * is already fetched centrally by `UserProvider` for any signed-in user (see its `enabled:
 * Boolean(user)`), so this hook just waits for that query's first resolution instead of
 * duplicating the round trip. If that fetch fails (`userStateError` with no `userState` to fall
 * back on), the pass is skipped entirely — mirroring the old hook's behavior of aborting rather
 * than risking a re-upload of local items as if the server had nothing.
 */

export interface SavedSearchItemPatch {
  alertEnabled?: boolean;
  label?: string | null;
  /** Id of the savedSearchFolder# this search belongs to; null clears it ("Unsorted"). */
  folderId?: string | null;
  /** Manual sort position within its folder group (Custom order sort mode). */
  order?: number;
}

export interface UseSavedSearchAccountSyncResult {
  /** Server item for a stored URL (any absolute/relative variant), if synced. Exact id match only. */
  getServerItem(url: string): ServerSavedSearch | undefined;
  /**
   * Server item that represents the same search as `url` (exact id first, then identity match:
   * same query and refinements regardless of sort, page, view, case or param order; alert-enabled
   * wins). See utils/saved-searches/find-saved-search-for-url.ts.
   */
  findServerItemForSearch(url: string): ServerSavedSearch | undefined;
  /** True once the initial upload/pull pass has finished. */
  synced: boolean;
  mutateItem(url: string, patch: SavedSearchItemPatch): Promise<boolean>;
  deleteItem(url: string): Promise<void>;
  /** All folders, keyed by id. */
  folders: Record<string, ServerSavedSearchFolder>;
  /** Creates a folder and returns its id, or null on failure. */
  createFolder(name: string): Promise<string | null>;
  renameFolder(id: string, name: string): Promise<boolean>;
  /** Persists a new folder ordering (index in the array = new `order`). */
  reorderFolders(orderedIds: string[]): Promise<void>;
  /** Deletes a folder; searches inside it fall back to "Unsorted" rather than being deleted. */
  deleteFolder(id: string): Promise<void>;
}

function savedSearchItemKey(id: string): UserStateSavedSearchKey {
  return `${SAVED_SEARCH_ITEM_KEY_PREFIX}${id}`;
}

function savedSearchFolderItemKey(id: string): UserStateSavedSearchFolderKey {
  return `${SAVED_SEARCH_FOLDER_ITEM_KEY_PREFIX}${id}`;
}

/**
 * `/search/{id}` keeps a literal `#` out of the URL — see build-user-state-item-api-path.ts. Used
 * only by the stale-delete-retry pass below, which is a best-effort fire-and-forget cleanup
 * outside the mutation lifecycle (it deliberately doesn't touch the shared cache).
 */
function itemApiPath(id: string): string {
  return `/api/user-state/search/${encodeURIComponent(id)}`;
}

export function useSavedSearchAccountSync(
  enabled: boolean,
  savedSearches: SearchEntry[]
): UseSavedSearchAccountSyncResult {
  const dispatch = useDispatch();
  const { data: userState, isLoading: isLoadingUserState, error: userStateError } = useUserState();
  const updateUserStateItem = useUpdateUserStateItem();
  const deleteUserStateItem = useDeleteUserStateItem();
  const [synced, setSynced] = useState(false);
  const syncStartedRef = useRef(false);

  // Kept fresh on every render (not via an effect) so the settle-retry timer below always reads
  // the latest values instead of whatever was in scope when the one-shot pass was kicked off.
  const savedSearchesRef = useRef(savedSearches);
  savedSearchesRef.current = savedSearches;
  const userStateRef = useRef(userState);
  userStateRef.current = userState;

  // The server item (if any) this hook itself most recently created for the current in-progress
  // local search, i.e. the head of `savedSearches` at the time it was uploaded. A continuous
  // run of refinements ("head" -> "head of" -> "head of engineering") collapses into one local
  // entry (distillSearchHistory in utils/saved-searches/search-history-policy.ts, applied by the
  // reducer), but each settled URL along the way hashes to a different server item id, so
  // without this a session that outlived SAVE_SYNC_DEBOUNCE_MS mid-refinement would leave one
  // server item per pause instead of just its final one. Only ever points at something this hook
  // itself created (alertEnabled: false); a deliberate save is never tracked here and never
  // auto-deleted.
  const openSessionRef = useRef<{ id: string; url: string } | null>(null);

  const items = useMemo(() => {
    const result: Record<string, ServerSavedSearch> = {};
    for (const item of parseServerSavedSearches(userState ?? {})) {
      result[item.id] = item;
    }
    return result;
  }, [userState]);

  const folders = useMemo(() => {
    const result: Record<string, ServerSavedSearchFolder> = {};
    for (const folder of parseServerSavedSearchFolders(userState ?? {})) {
      result[folder.id] = folder;
    }
    return result;
  }, [userState]);

  useEffect(() => {
    if (!enabled || syncStartedRef.current) return;
    if (isLoadingUserState) return;
    syncStartedRef.current = true;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const runSyncPass = async () => {
      try {
        const currentUserState = userStateRef.current;
        if (userStateError && currentUserState === undefined) {
          // Offline/unauthed: the page still works from local state.
          return;
        }
        const rawServer = parseServerSavedSearches(currentUserState ?? {});

        // A recently-deleted item may still be on the server if its DELETE request
        // failed or was cut short by a refresh; retry it instead of pulling it back.
        const staleDeletes = rawServer.filter((item) => isRecentlySavedSearchDeleted(item.id));
        staleDeletes.forEach((item) => {
          void fetch(itemApiPath(item.id), { method: 'DELETE', keepalive: true }).catch(() => {});
        });
        const server = rawServer.filter((item) => !staleDeletes.includes(item));

        const plan = computeSavedSearchSyncPlan(savedSearchesRef.current, server);

        for (const upload of plan.uploads) {
          try {
            await updateUserStateItem.mutateAsync({
              itemKey: savedSearchItemKey(upload.id),
              data: { url: upload.url, alertEnabled: false, createdAt: upload.timestamp },
              version: 0,
            });
          } catch {
            // A failed upload retries on the next pass; sync is idempotent.
          }
        }

        if (cancelled) return;

        // Session bookkeeping: if the search we last uploaded ourselves is no longer the current
        // head, that session has either moved on to a new URL (refined away: the reducer's
        // distillSearchHistory pass removed the old entry outright) or been superseded by an
        // unrelated new search (the old entry is still sitting further down the list). Only the
        // first case means the old server item is now dead weight worth deleting; the second case
        // is just a normal finished search from earlier and must be left alone. Never touches an
        // item the server itself reports as alertEnabled — that means the user deliberately saved
        // it in the meantime (e.g. from the Searches & Alerts hub), which this passive path must
        // never undo.
        const openSession = openSessionRef.current;
        const currentHead = savedSearchesRef.current[0] as SearchEntry | undefined;
        if (openSession && openSession.url !== currentHead?.url) {
          const stillPresentLocally = savedSearchesRef.current.some((entry) => entry.url === openSession.url);
          if (!stillPresentLocally) {
            const stillAuto = server.find((item) => item.id === openSession.id)?.alertEnabled !== true;
            if (stillAuto) {
              try {
                await deleteUserStateItem.mutateAsync({ itemKey: savedSearchItemKey(openSession.id) });
              } catch {
                // Best-effort: a stray superseded item is harmless and can be cleaned up later.
              }
            }
          }
          openSessionRef.current = null;
        }
        if (currentHead) {
          const headUpload = plan.uploads.find((upload) => upload.url === currentHead.url);
          if (headUpload) {
            openSessionRef.current = { id: headUpload.id, url: currentHead.url };
          }
        }

        if (plan.changed) {
          dispatch(setSavedSearches(plan.mergedLocal));
        }

        // Some local searches aren't settled yet (see SAVE_SYNC_DEBOUNCE_MS) — check back once
        // the earliest of them would be, so a search gets uploaded shortly after the user
        // actually stops touching it instead of waiting for some unrelated page to remount this
        // hook. If it gets touched again before then, the next pass just recomputes a later one.
        if (plan.nextSettleCheckAt != null) {
          const delay = Math.max(0, plan.nextSettleCheckAt - Date.now());
          retryTimer = setTimeout(() => {
            if (!cancelled) void runSyncPass();
          }, delay);
        }
      } catch {
        // Offline/unauthed: the page still works from local state.
      } finally {
        if (!cancelled) setSynced(true);
      }
    };

    void runSyncPass();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
    // savedSearches/userState deliberately read via refs, not deps: this still kicks off as a
    // one-shot pass per enablement (later mutations write through directly), but the settle
    // check above re-invokes itself on a timer rather than needing a dep change to re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isLoadingUserState, userStateError, dispatch]);

  const getServerItem = useCallback(
    (url: string) => items[savedSearchAlertId(url)],
    [items]
  );

  const findServerItemForSearch = useCallback(
    (url: string) => findSavedSearchForUrl(items, url),
    [items]
  );

  const mutateItem = useCallback(
    async (url: string, patch: SavedSearchItemPatch): Promise<boolean> => {
      const id = savedSearchAlertId(url);
      const existing = items[id];

      const label = patch.label !== undefined ? patch.label : existing?.label;
      const folderId = patch.folderId !== undefined ? patch.folderId : existing?.folderId;
      const order = patch.order !== undefined ? patch.order : existing?.order;
      const data = {
        url: existing?.url ?? canonicalSavedSearchPath(url),
        alertEnabled: patch.alertEnabled ?? existing?.alertEnabled ?? false,
        createdAt: existing?.createdAt ?? Date.now(),
        ...(label ? { label } : {}),
        ...(folderId ? { folderId } : {}),
        ...(typeof order === 'number' ? { order } : {}),
      };

      try {
        await updateUserStateItem.mutateAsync({
          itemKey: savedSearchItemKey(id),
          data,
          version: existing?.version ?? 0,
        });
        return true;
      } catch {
        // On a 409, the mutation's onError already wrote the server's winning entry into the
        // shared cache — items above reflects it as soon as that resolves. See the file-level
        // doc comment for why this doesn't retry the patch on top of the fresh base.
        return false;
      }
    },
    [items, updateUserStateItem]
  );

  const deleteItem = useCallback(
    async (url: string): Promise<void> => {
      const id = savedSearchAlertId(url);
      const existing = items[id];
      recordSavedSearchDeleted(id);
      try {
        // keepalive-equivalent durability comes from useDeleteUserStateItem's own mutation
        // lifecycle; the tombstone above covers the case where the request still fails.
        await deleteUserStateItem.mutateAsync({ itemKey: savedSearchItemKey(id), version: existing?.version });
        clearSavedSearchDeleted(id);
      } catch {
        // Tombstone keeps the item from being pulled back until a later sync retries it.
      }
    },
    [items, deleteUserStateItem]
  );

  const upsertFolder = useCallback(
    async (
      id: string,
      patch: { name?: string; order?: number },
      existing?: ServerSavedSearchFolder
    ): Promise<ServerSavedSearchFolder | null> => {
      const data: SavedSearchFolderData = {
        name: patch.name !== undefined ? patch.name : existing?.name ?? '',
        order: patch.order !== undefined ? patch.order : existing?.order ?? 0,
        createdAt: existing?.createdAt ?? Date.now(),
      };

      try {
        const result = await updateUserStateItem.mutateAsync({
          itemKey: savedSearchFolderItemKey(id),
          data,
          version: existing?.version ?? 0,
        });
        return {
          id,
          name: data.name,
          order: data.order,
          createdAt: data.createdAt,
          version: result.version,
        };
      } catch {
        return null;
      }
    },
    [updateUserStateItem]
  );

  const createFolder = useCallback(
    async (name: string): Promise<string | null> => {
      const trimmed = name.trim();
      if (!trimmed) return null;
      const id = crypto.randomUUID();
      const order = Object.values(folders).reduce((max, f) => Math.max(max, f.order), -1) + 1;
      const result = await upsertFolder(id, { name: trimmed, order });
      return result ? id : null;
    },
    [folders, upsertFolder]
  );

  const renameFolder = useCallback(
    async (id: string, name: string): Promise<boolean> => {
      const trimmed = name.trim();
      if (!trimmed) return false;
      const result = await upsertFolder(id, { name: trimmed }, folders[id]);
      return result !== null;
    },
    [folders, upsertFolder]
  );

  const reorderFolders = useCallback(
    async (orderedIds: string[]): Promise<void> => {
      await Promise.all(
        orderedIds.map((id, index) => {
          const existing = folders[id];
          if (!existing || existing.order === index) return Promise.resolve(null);
          return upsertFolder(id, { order: index }, existing);
        })
      );
    },
    [folders, upsertFolder]
  );

  const deleteFolder = useCallback(
    async (id: string): Promise<void> => {
      const affected = Object.values(items).filter((item) => item.folderId === id);
      await Promise.all(affected.map((item) => mutateItem(item.url, { folderId: null })));
      const existing = folders[id];
      try {
        await deleteUserStateItem.mutateAsync({ itemKey: savedSearchFolderItemKey(id), version: existing?.version });
      } catch {
        // Best-effort; a stale folder item is harmless and can be deleted again later.
      }
    },
    [items, folders, mutateItem, deleteUserStateItem]
  );

  return {
    getServerItem,
    findServerItemForSearch,
    synced,
    mutateItem,
    deleteItem,
    folders,
    createFolder,
    renameFolder,
    reorderFolders,
    deleteFolder,
  };
}
